import React from "react";

import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Button } from "@/components/ui/button";

export type DeleteReportsDialogRequest =
  | {
      mode: "single";
      reportId: string;
    }
  | {
      mode: "bulk";
      reportIds: string[];
    };

export type DeleteReportsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: DeleteReportsDialogRequest | null;
  isDeleting?: boolean;
  onConfirm: () => void;
};

function deleteReportsCount(request: DeleteReportsDialogRequest): number {
  return request.mode === "bulk" ? request.reportIds.length : 1;
}

function deleteReportsMessage(request: DeleteReportsDialogRequest): string {
  if (request.mode === "bulk") {
    const count = request.reportIds.length;
    return `Delete ${count} selected report${count === 1 ? "" : "s"} permanently? This cannot be undone.`;
  }
  return "Delete this report permanently? This cannot be undone.";
}

export function DeleteReportsDialog({
  open,
  onOpenChange,
  request,
  isDeleting = false,
  onConfirm,
}: DeleteReportsDialogProps): React.ReactElement {
  const reportCount = request ? deleteReportsCount(request) : 0;
  const dialogTitle = reportCount === 1 ? "Delete report" : "Delete reports";
  const deleteButtonLabel =
    reportCount === 1 ? "Delete report" : "Delete reports";
  const message = request ? deleteReportsMessage(request) : "";

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isDeleting) return;
    onOpenChange(nextOpen);
  };

  const handleCancel = () => {
    if (isDeleting) return;
    onOpenChange(false);
  };

  return (
    <StrategyPineDetailDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={dialogTitle}
      showHeader={false}
      showDismissButton={false}
      contentClassName="sm:max-w-md"
      bodyClassName="flex-none overflow-visible pt-0"
      contentProps={{ "data-reports-delete-dialog": "" }}
      footer={
        <div
          data-dashboard-layout-dialog-footer=""
          className="inline-flex max-w-full flex-wrap items-center justify-end gap-2"
        >
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            className="shrink-0"
            disabled={isDeleting}
            onClick={handleCancel}
          >
            <span className="normal-case tracking-normal">Cancel</span>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-trade-log-action="delete"
            className="shrink-0 border-destructive/40 text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive/60 focus-visible:ring-destructive/25 dark:border-destructive/50 dark:hover:bg-destructive/15 [&_svg]:text-current"
            disabled={isDeleting || request == null}
            aria-busy={isDeleting || undefined}
            onClick={onConfirm}
          >
            <span data-font="primary" className="normal-case tracking-normal">
              {isDeleting ? "Deleting…" : deleteButtonLabel}
            </span>
          </Button>
        </div>
      }
    >
      <p className="font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500 dark:text-sidebar-foreground">
        {message}
      </p>
    </StrategyPineDetailDialog>
  );
}
